<template>
  <div class="forwarder-manager">
    <!-- Loading Indicator -->
    <div v-if="loading" class="mb-2">
      <BaseMiniSpinner />
    </div>

    <!-- No Forwarder Selected -->
    <div v-if="!forwarderInfoautoId" class="flex flex-col p-4 gap-2">
      <p v-if="!canEdit" class="text-gray-500">
        {{ $t("message.order.no_forwarder") }}
      </p>
      <template v-else>
        <button
          class="btn btn-dark-button w-80 text-center"
          @click="$emit('select-forwarder')"
        >
          {{ $t("message.order.select_forwarder_from_infoauto") }}
        </button>
        <button
          @click="$emit('use-order-customer')"
          class="btn btn-dark-button w-80 text-center"
        >
          {{ $t("message.order.same_customer_in_order") }}
        </button>
      </template>
    </div>

    <!-- Forwarder Selected -->
    <div v-else class="flex flex-col p-4">
      <!-- Forwarder Info -->
      <div class="flex flex-row gap-2 mb-4">
        <b class="font-bold">{{ forwarderName }}</b>
        <template v-if="canEdit">
          <button class="btn-error-button" @click="$emit('select-forwarder')">
            {{ $t("message.order.change_forwarder") }}
          </button>
          <button class="btn-error-button" @click="$emit('remove-forwarder')">
            {{ $t("message.order.remove_forwarder") }}
          </button>
        </template>
      </div>

      <!-- Address Selection -->
      <div class="select-grid" v-if="canEdit">
        <select
          @change="handleAddressChange"
          class="regular full bordered"
          v-model="selectedAddressIndex"
        >
          <option value="">
            {{ $t("message.order.select_forwarder_address") }} ({{
              addressOptions.length
            }})
          </option>
          <option
            v-for="(address, index) in addressOptions"
            :key="index"
            :value="index"
          >
            {{ address.street1 }} - {{ address.city }}
          </option>
        </select>
        <button class="btn-dark-button" @click="$emit('create-address')">
          {{ $t("message.order.new_address") }}
        </button>
      </div>

      <!-- Phone Selection -->
      <div class="select-grid mt-3" v-if="canEdit">
        <select
          @change="handlePhoneChange"
          class="regular full bordered"
          v-model="selectedPhoneIndex"
        >
          <option value="">
            {{ $t("message.order.select_forwarder_phone") }} ({{
              phoneOptions.length
            }})
          </option>
          <option
            v-for="(phone, index) in phoneOptions"
            :key="index"
            :value="index"
          >
            {{ formatPhone(phone.areaCode, phone.number) }}
          </option>
        </select>
        <button class="btn-dark-button" @click="$emit('create-phone')">
          {{ $t("message.order.new_phone") }}
        </button>
      </div>

      <!-- Address Preview -->
      <div class="py-2 mt-4">
        <button
          v-if="canEdit && hasChanges && currentAddress"
          class="btn btn-danger-button text-red-500 font-bold mb-2"
          @click="clearAddressAndPhone"
        >
          {{ $t("message.order.remove_address_and_phone") }}
        </button>

        <div v-if="currentAddress" class="p-2 border rounded-mb my-1">
          <p>{{ $t("message.order.name") }}: {{ currentAddress.name }}</p>
          <p>{{ $t("message.order.address") }}: {{ currentAddress.street1 }}</p>
          <p>{{ $t("message.order.city") }}: {{ currentAddress.city }}</p>
          <p>{{ $t("message.order.state") }}: {{ currentAddress.state }}</p>
          <p>{{ $t("message.order.country") }}: {{ currentAddress.country }}</p>
          <p>
            {{ $t("message.order.postal_code") }}:
            {{ currentAddress.postalCode }}
          </p>
        </div>
        <p v-else class="text-red-500 p-2 border rounded-mb my-1">
          {{ $t("message.order.address_pending") }}
        </p>

        <p v-if="currentPhone" class="p-2 border rounded-mb my-1">
          {{ $t("message.order.phone") }}:
          {{ formatPhone(currentPhone.areaCode, currentPhone.number) }}
        </p>
        <p v-else class="text-red-500 p-2 border rounded-mb my-1">
          {{ $t("message.order.phone_pending") }}
        </p>
      </div>

      <!-- Defaults: the forwarder's own address/phone, the customer's forwarder -->
      <div
        class="flex flex-col items-start gap-2"
        v-if="canEdit && !hasChanges"
      >
        <SaveDefaultButton
          v-if="canSaveAddressDefault"
          :disabled="saving"
          @click="$emit('save-default', 'forwarder_address')"
        >
          {{ $t("message.order.save_forwarder_address_default") }}
        </SaveDefaultButton>
        <SaveDefaultButton
          v-if="canSaveCustomerForwarder"
          :disabled="saving"
          @click="$emit('save-default', 'forwarder')"
        >
          {{ $t("message.order.save_customer_forwarder_default") }}
        </SaveDefaultButton>
      </div>

      <!-- Save/Cancel Buttons -->
      <div class="flex flex-row mt-4 gap-2" v-if="hasChanges">
        <button
          class="btn btn-success-button"
          @click="saveChanges"
          :disabled="saving"
        >
          {{ saving ? $t("message.order.saving") : $t("common.save") }}
        </button>
        <button
          class="btn btn-dark-button"
          @click="discardChanges"
          v-if="!saving"
        >
          {{ $t("message.order.discard") }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { formatPhone } from "@/utils/phone";
import SaveDefaultButton from "./SaveDefaultButton.vue";

export default {
  name: "ForwarderManager",
  components: { SaveDefaultButton },
  props: {
    forwarderInfoautoId: {
      type: [String, Number],
      default: null
    },
    forwarderName: {
      type: String,
      default: ""
    },
    addressOptions: {
      type: Array,
      default: () => []
    },
    phoneOptions: {
      type: Array,
      default: () => []
    },
    address: {
      type: Object,
      default: null
    },
    // { areaCode, number }
    phone: {
      type: Object,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    },
    saving: {
      type: Boolean,
      default: false
    },
    // False on orders that cannot be edited (e.g. a converted quote): only
    // shows the forwarder
    canEdit: {
      type: Boolean,
      default: true
    },
    // The saved address/phone differ from the forwarder's default
    canSaveAddressDefault: {
      type: Boolean,
      default: false
    },
    // The order's forwarder is not the customer's usual one
    canSaveCustomerForwarder: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      currentAddress: null,
      currentPhone: null,
      originalAddress: null,
      originalPhone: null,
      selectedAddressIndex: "",
      selectedPhoneIndex: ""
    };
  },
  computed: {
    hasChanges() {
      if (!this.forwarderInfoautoId) {
        return false;
      }
      return (
        JSON.stringify(this.currentAddress) !== this.originalAddress ||
        JSON.stringify(this.currentPhone) !== this.originalPhone
      );
    }
  },
  watch: {
    // Also when it goes back to null: the order reloaded without one
    address: {
      handler(val) {
        this.currentAddress = val ? { ...val } : null;
        this.originalAddress = JSON.stringify(val || null);
        this.selectedAddressIndex = "";
      },
      immediate: true
    },
    phone: {
      handler(val) {
        this.currentPhone = val ? { ...val } : null;
        this.originalPhone = JSON.stringify(val || null);
        this.selectedPhoneIndex = "";
      },
      immediate: true
    }
  },
  methods: {
    formatPhone,
    handleAddressChange() {
      if (this.selectedAddressIndex !== "") {
        const selected = this.addressOptions[this.selectedAddressIndex];
        this.currentAddress = {
          infoautoAddressId: selected.infoautoAddressId || "",
          name: selected.name,
          company: selected.company,
          street1: selected.street1,
          street2: selected.street2,
          street3: selected.street3,
          city: selected.city,
          state: selected.state,
          country: selected.country,
          postalCode: selected.postalCode,
          residential: selected.residential
        };
      }
    },
    handlePhoneChange() {
      if (this.selectedPhoneIndex !== "") {
        this.currentPhone = { ...this.phoneOptions[this.selectedPhoneIndex] };
      }
    },
    clearAddressAndPhone() {
      this.currentAddress = null;
      this.currentPhone = null;
    },
    saveChanges() {
      this.$emit("save", {
        address: this.currentAddress,
        phone: this.currentPhone?.number || "",
        phoneAreaCode: this.currentPhone?.areaCode || "",
        infoautoPhoneId: this.currentPhone?.infoautoPhoneId || ""
      });
    },
    discardChanges() {
      if (this.originalAddress) {
        this.currentAddress = JSON.parse(this.originalAddress);
      } else {
        this.currentAddress = null;
      }
      this.currentPhone = this.originalPhone
        ? JSON.parse(this.originalPhone)
        : null;
      this.selectedAddressIndex = "";
      this.selectedPhoneIndex = "";
    },
    // Public methods to be called by parent
    setAddress(address) {
      this.currentAddress = { ...address };
      this.selectedAddressIndex = "";
    },
    setPhone(phone) {
      this.currentPhone = { ...phone };
      this.selectedPhoneIndex = "";
    }
  }
};
</script>

<style scoped>
.forwarder-manager {
  width: 100%;
}

.select-grid {
  display: grid;
  grid-template-columns: 1fr 140px;
  gap: 1rem;
}
</style>
